import { Fragment, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { RULES_ROUTE, useAppSelector } from "@hypexone/uicore";
import { IconType } from "react-icons";
import { FaInstagram, FaPaperPlane } from "react-icons/fa6";
import { IoLogoWhatsapp } from "react-icons/io5";
import { RiCustomerService2Fill } from "react-icons/ri";

// Footer policy links. Only Rules has a page in this app today; the rest need a
// destination before they navigate — set `href` here once those exist.
export const FOOTER_POLICY_LINKS: { label: string; href?: string }[] = [
  { label: "Privacy Policy" },
  { label: "Terms and Conditions" },
  { label: "Rules and Regulations", href: RULES_ROUTE },
  { label: "KYC" },
  { label: "Responsible Gaming" },
  { label: "About Us" },
  { label: "Self-Exclusion Policy" },
  { label: "Underage Policy" },
];

const FOOTER_CONTACT_GROUPS: {
  match: string;
  Icon: IconType;
  fullWidth?: boolean;
  // Placeholder rows shown until support_contacts is configured
  dummy: { label: string; href: string }[];
}[] = [
  {
    match: "WHATSAPP",
    Icon: IoLogoWhatsapp,
    fullWidth: true,
    dummy: [
      { label: "WhatsApp 3", href: "#" },
      { label: "WhatsApp 4", href: "#" },
    ],
  },
  {
    match: "SUPPORT",
    Icon: RiCustomerService2Fill,
    fullWidth: true,
    dummy: [
      { label: "Customer support1", href: "#" },
      { label: "support2", href: "#" },
    ],
  },
  {
    match: "EMAIL",
    Icon: FaPaperPlane,
    dummy: [{ label: "Email", href: "#" }],
  },
  {
    match: "INSTAGRAM",
    Icon: FaInstagram,
    dummy: [{ label: "skyexchindia", href: "#" }],
  },
];

/** Policy links. `pipes` stacks with `|` dividers, `dashes` is one hyphen row. */
export const PolicyLinks = ({
  variant = "pipes",
  className = "",
}: {
  variant?: "pipes" | "dashes";
  className?: string;
}) => {
  const navigate = useNavigate();

  if (variant === "dashes") {
    return (
      <div
        className={`flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2 text-[12px] leading-[120%] text-[#666] text-center ${className}`}
      >
        {FOOTER_POLICY_LINKS.map((link) => (
          <Fragment key={link.label}>
            <span aria-hidden="true">-</span>
            <span
              onClick={() => link.href && navigate(link.href)}
              className={`inline-block underline whitespace-nowrap ${
                link.href ? "cursor-pointer" : ""
              }`}
            >
              {link.label}
            </span>
          </Fragment>
        ))}
        <span aria-hidden="true">-</span>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap justify-center text-[14px] leading-[120%] text-[#3f3939] text-center gap-y-2.5 tracking-[0.04em] ${className}`}
    >
      {FOOTER_POLICY_LINKS.map((link, index) => (
        <span
          key={link.label}
          onClick={() => link.href && navigate(link.href)}
          className={`inline-block underline whitespace-nowrap px-2 ${
            link.href ? "cursor-pointer" : ""
          } ${
            index < FOOTER_POLICY_LINKS.length - 1
              ? "border-r border-[#999]"
              : ""
          }`}
        >
          {link.label}
        </span>
      ))}
    </div>
  );
};

/** WhatsApp / support / email / instagram cards, driven by support_contacts. */
export const ContactCards = ({
  className = "",
  cardClassName = "border border-[#999] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]",
  emailSlot,
}: {
  className?: string;
  cardClassName?: string;
  emailSlot?: ReactNode;
}) => {
  const { domainConfig, whatsappDetails } = useAppSelector(
    (state) => state.common,
  );
  const { loggedIn } = useAppSelector((state) => state.auth);

  // contactType strings are set per house by the backend, so groups match on a
  // substring rather than an exact key.
  const contactsMatching = (match: string): string[] => {
    const configured = (domainConfig?.suppportContacts ?? [])
      .filter((ct: { contactType?: string; details?: string }) =>
        ct?.contactType?.toUpperCase().includes(match),
      )
      .map((ct: { details?: string }) => ct?.details)
      .filter(Boolean);

    if (match === "WHATSAPP" && loggedIn && whatsappDetails) {
      return [whatsappDetails, ...configured];
    }
    return configured;
  };

  const contactHref = (group: string, value: string) =>
    group === "EMAIL" && !value.startsWith("mailto:") && value.includes("@")
      ? `mailto:${value}`
      : value;

  return (
    <div className={`grid grid-cols-2 gap-2.5 ${className}`}>
      {FOOTER_CONTACT_GROUPS.map(({ match, Icon, fullWidth, dummy }) => {
        if (match === "EMAIL" && emailSlot) {
          return (
            <div
              key={match}
              className="col-span-1 flex h-full items-center justify-center overflow-hidden"
            >
              {emailSlot}
            </div>
          );
        }

        // Labels come from the placeholder rows; each one links to the matching
        // configured contact, falling back to the first.
        const configured = contactsMatching(match);
        const entries = dummy.map((row, index) => {
          const value = configured[index] ?? configured[0];
          return {
            label: row.label,
            href: value ? contactHref(match, value) : row.href,
          };
        });

        return (
          <div
            key={match}
            className={`${
              fullWidth ? "col-span-2" : "col-span-1"
            } flex items-center justify-center gap-2.5 rounded-lg px-5 py-3 ${cardClassName}`}
          >
            <Icon className="h-[22px] w-[22px] shrink-0 text-[#555]" />
            {entries.map((entry, index) => (
              <div key={entry.label} className="flex items-center">
                {index > 0 && <span className="mx-3.5 h-4 w-px bg-[#d6d6d6]" />}
                <a
                  href={entry.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[14px] text-[#666] hover:text-[#333]"
                >
                  {entry.label}
                </a>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
